import * as THREE from 'three'
import { buildPaletteLUT } from './palette'
import type { BlendMode } from '../types'

const BLEND_CODE: Record<BlendMode, number> = { normal: 0, multiply: 1, screen: 2, add: 3 }

/** Visual settings for the recoloring (everything that isn't the video source). */
export interface VideoLabAppearance {
  palette: string[]
  /** Number of color IDs (bands) in discrete mode. */
  bands: number
  /** true = smooth gradient; false = discrete bands. */
  smooth: boolean
  /** Gray↔color blend (0 = original gray, 1 = full recoloring). */
  intensity: number
  /** Black point (0..1): grays below it are mapped to the first color. */
  black: number
  /** White point (0..1): grays above it are mapped to the last color. */
  white: number
  /** Edge smoothing (0..1): blurs the luminance before quantizing to clean band edges. */
  smoothing: number
  /** Blend mode of the palette color over the source. */
  blend: BlendMode
}

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`

const FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uVideo;
  uniform sampler2D uLUT;
  uniform float uIntensity;
  uniform float uBlack;
  uniform float uWhite;
  uniform float uBlur;   // luminance blur radius in texels (0 = off)
  uniform vec2 uTexel;   // 1 / video resolution
  uniform float uBlend;  // 0 normal, 1 multiply, 2 screen, 3 add
  varying vec2 vUv;

  vec3 blendColor(vec3 base, vec3 top) {
    int m = int(uBlend + 0.5);
    if (m == 1) return base * top;
    if (m == 2) return 1.0 - (1.0 - base) * (1.0 - top);
    if (m == 3) return min(vec3(1.0), base + top);
    return top;
  }

  // Video luminance (robust even if the "gray" has a slight tint).
  float lum(vec2 uv) {
    return dot(texture2D(uVideo, uv).rgb, vec3(0.299, 0.587, 0.114));
  }

  void main() {
    // Optional blur on the luminance BEFORE quantizing: smooths the band edges
    // (cleaner tone curves) without losing the flat color separation.
    float g;
    if (uBlur < 0.01) {
      g = lum(vUv);
    } else {
      vec2 o = uTexel * uBlur;
      g = lum(vUv) * 0.25
        + (lum(vUv + vec2(o.x, 0.0)) + lum(vUv - vec2(o.x, 0.0))
         + lum(vUv + vec2(0.0, o.y)) + lum(vUv - vec2(0.0, o.y))) * 0.125
        + (lum(vUv + o) + lum(vUv - o)
         + lum(vUv + vec2(o.x, -o.y)) + lum(vUv - vec2(o.x, -o.y))) * 0.0625;
    }
    // Levels remapping (black/white points).
    g = clamp((g - uBlack) / max(1e-4, uWhite - uBlack), 0.0, 1.0);
    vec3 mapped = texture2D(uLUT, vec2(g, 0.5)).rgb;
    gl_FragColor = vec4(mix(vec3(g), blendColor(vec3(g), mapped), uIntensity), 1.0);
  }
`

/**
 * Live recoloring pipeline: takes a grayscale video and remaps it to a palette
 * via a LUT, rendering to a WebGLRenderTarget whose texture is used as the scene
 * background (and as the LED primitive's source).
 *
 * The video is stored only once; the palette is changed instantly by rebuilding
 * the LUT on the CPU. No video re-encoding.
 */
export class VideoLabRecolor {
  private scene = new THREE.Scene()
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  private quad: THREE.Mesh
  private material: THREE.ShaderMaterial
  private lut: THREE.DataTexture
  private rt: THREE.WebGLRenderTarget

  private videoEl: HTMLVideoElement | null = null
  private videoTex: THREE.VideoTexture | null = null
  private dummy: THREE.DataTexture
  private hasSource = false

  constructor(private appearance: VideoLabAppearance) {
    this.dummy = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat)
    this.dummy.needsUpdate = true

    this.lut = buildPaletteLUT(appearance.palette, appearance.bands, appearance.smooth)

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uVideo: { value: this.dummy },
        uLUT: { value: this.lut },
        uIntensity: { value: appearance.intensity },
        uBlack: { value: appearance.black },
        uWhite: { value: appearance.white },
        uBlur: { value: appearance.smoothing * 4 },
        uTexel: { value: new THREE.Vector2(1 / 1280, 1 / 720) },
        uBlend: { value: BLEND_CODE[appearance.blend] ?? 0 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
    })

    // Fullscreen triangle/quad in clip-space (the ortho camera is irrelevant).
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material)
    this.quad.frustumCulled = false
    this.scene.add(this.quad)

    this.rt = new THREE.WebGLRenderTarget(1280, 720, {
      depthBuffer: false,
      stencilBuffer: false,
    })
    this.rt.texture.colorSpace = THREE.SRGBColorSpace
  }

  get active(): boolean {
    return this.hasSource
  }

  /** Recolored texture (valid after calling render()). */
  get texture(): THREE.Texture {
    return this.rt.texture
  }

  /** The underlying source <video> (for loop-style playback control), if any. */
  get videoElement(): HTMLVideoElement | null {
    return this.videoEl
  }

  /** Aspect ratio (width/height) of the recolored video. */
  get aspect(): number {
    return this.rt.height > 0 ? this.rt.width / this.rt.height : 1
  }

  /** Loads the gray video (hidden in the DOM to avoid decoding throttling). */
  setSource(url: string | null) {
    this.disposeVideo()
    if (!url) {
      this.hasSource = false
      this.material.uniforms.uVideo.value = this.dummy
      return
    }

    const video = document.createElement('video')
    video.src = url
    video.crossOrigin = 'anonymous'
    video.loop = true
    video.muted = true
    video.autoplay = true
    video.playsInline = true
    video.preload = 'auto'
    video.style.cssText =
      'position:fixed;left:-10px;top:-10px;width:1px;height:1px;opacity:0;pointer-events:none;'
    document.body.appendChild(video)
    const keepPlaying = () => void video.play().catch(() => {})
    // Resume on throttling pauses — but NOT when the pause is managed (ping-pong
    // reverse pauses on purpose to seek backward; see Scene.updateVideoLoop).
    video.addEventListener('pause', () => {
      const managed = (video as HTMLVideoElement & { __managedPause?: boolean }).__managedPause
      if (video.isConnected && !video.ended && !managed) keepPlaying()
    })
    video.addEventListener('loadedmetadata', () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        this.rt.setSize(video.videoWidth, video.videoHeight)
        // Blur step is in texels of the real video resolution.
        this.material.uniforms.uTexel.value.set(1 / video.videoWidth, 1 / video.videoHeight)
      }
    })
    keepPlaying()

    const tex = new THREE.VideoTexture(video)
    tex.colorSpace = THREE.SRGBColorSpace
    this.videoEl = video
    this.videoTex = tex
    this.material.uniforms.uVideo.value = tex
    this.hasSource = true
  }

  /** Updates palette/bands/mode/intensity/levels without touching the video source. */
  setAppearance(a: VideoLabAppearance) {
    const paletteChanged =
      a.bands !== this.appearance.bands ||
      a.smooth !== this.appearance.smooth ||
      a.palette.length !== this.appearance.palette.length ||
      a.palette.some((c, i) => c !== this.appearance.palette[i])
    this.appearance = a
    if (paletteChanged) {
      buildPaletteLUT(a.palette, a.bands, a.smooth, this.lut)
    }
    this.material.uniforms.uIntensity.value = a.intensity
    this.material.uniforms.uBlack.value = a.black
    this.material.uniforms.uWhite.value = a.white
    this.material.uniforms.uBlur.value = a.smoothing * 4
    this.material.uniforms.uBlend.value = BLEND_CODE[a.blend] ?? 0
  }

  /** Renders the recoloring to the render target. Restores the renderer's target. */
  render(renderer: THREE.WebGLRenderer) {
    if (!this.hasSource) return
    const prev = renderer.getRenderTarget()
    renderer.setRenderTarget(this.rt)
    renderer.render(this.scene, this.camera)
    renderer.setRenderTarget(prev)
  }

  private disposeVideo() {
    if (this.videoTex) {
      this.videoTex.dispose()
      this.videoTex = null
    }
    if (this.videoEl) {
      this.videoEl.pause()
      this.videoEl.src = ''
      this.videoEl.remove()
      this.videoEl = null
    }
  }

  dispose() {
    this.disposeVideo()
    this.lut.dispose()
    this.dummy.dispose()
    this.material.dispose()
    this.quad.geometry.dispose()
    this.rt.dispose()
  }
}
